import { createSelector } from 'reselect';

// Types
import { RootState } from 'types/RootState.types';

// Config
import featureConstants from '@constants/featureConstants';
import { getAddressBookAPICallActiveSelector } from '@redux/reducers/addressBook/addressBookSelector';
import { getIsExpressCheckoutSelector, getLoadingLazyComponentSelector } from '@redux/reducers/app/appSelector';
import { getSessionIdSelector } from '@redux/reducers/bff/bffSelector';
import { isFeatureActive } from '@redux/reducers/config/configReducer';
import { getLoqateApiKeySelector } from '@redux/reducers/config/configSelector';
import {
  getConfirmedDeliveryAddressSelector,
  getAddressSearchCompleteSelector,
  getDeliveryContainsUndeliverableServicesSelector,
  getDeliveryContainsIncompatibleServicesSelector,
  getServicesThatNeedRemovingSelector,
  getUndeliverableItemsInBasketSelector,
  getSaveAddressErrorSelector,
  getPutDeliveryAddressApiCallActiveSelector,
  getDeliveriesSelector,
  getShowAddressBookModalSelector,
  getIncompleteDeliveryDetailsError,
  getDeliveryProceedingToPaymentSelector,
  getShouldReFetchDeliveryOptions,
  getCreateAddressAPICallActiveSelector,
  getDeliveryPutDeliveryAddressErrorSelector,
  getIsChangingAddressSelector,
} from '@redux/reducers/delivery/deliverySelector';
import { getFormSelector } from '@redux/reducers/form/formSelector';
import { getOrderFormItemsSelector } from '@redux/reducers/order-form/orderFormSelector';
import { getRouterLocationPathname } from '@redux/reducers/router/routerSelector';
import {
  getAddressBookSelector,
  getSelectedDeliveryAddressSelector,
  getCanUseSelectedDeliveryAddressSelector,
  getIsSignedInSelector,
  getIsSignedInWithDataSelector,
  getPostcodeOutOfAreaErrorSelector,
  getUserEmailAddressSelector,
} from '@redux/reducers/user/userSelector';
import { DELIVERY_ADDRESS_FORM_ID } from '@utils/form/configs/deliveryAddress';

const getIsNewAddressBookLayoutFeatureFlag = (state: RootState) =>
  isFeatureActive(state, featureConstants.NEW_ADDRESS_BOOK_LAYOUT);

const deliveryAddressState = createSelector(
  [
    getAddressBookAPICallActiveSelector,
    getAddressBookSelector,
    getAddressSearchCompleteSelector,
    getCanUseSelectedDeliveryAddressSelector,
    getConfirmedDeliveryAddressSelector,
    getCreateAddressAPICallActiveSelector,
    getDeliveriesSelector,
    getDeliveryContainsIncompatibleServicesSelector,
    getDeliveryContainsUndeliverableServicesSelector,
    getDeliveryProceedingToPaymentSelector,
    getDeliveryPutDeliveryAddressErrorSelector,
    getFormSelector,
    getIncompleteDeliveryDetailsError,
    getIsChangingAddressSelector,
    getIsExpressCheckoutSelector,
    getIsNewAddressBookLayoutFeatureFlag,
    getIsSignedInSelector,
    getIsSignedInWithDataSelector,
    getLoadingLazyComponentSelector,
    getLoqateApiKeySelector,
    getOrderFormItemsSelector,
    getPostcodeOutOfAreaErrorSelector,
    getPutDeliveryAddressApiCallActiveSelector,
    getRouterLocationPathname,
    getSaveAddressErrorSelector,
    getSelectedDeliveryAddressSelector,
    getServicesThatNeedRemovingSelector,
    getSessionIdSelector,
    getShouldReFetchDeliveryOptions,
    getShowAddressBookModalSelector,
    getUndeliverableItemsInBasketSelector,
    getUserEmailAddressSelector,
  ],
  (
    addressBookAPICallActive,
    addressBook,
    addressSearchComplete,
    canUseSelectedDeliveryAddress,
    confirmedDeliveryAddress,
    createAddressAPICallActive,
    deliveries,
    deliveryContainsIncompatibleServices,
    deliveryContainsUndeliverableServices,
    proceedingToPayment,
    putDeliveryAddressError,
    form,
    incompleteDeliveryDetailsError,
    isChangingAddress,
    isExpressCheckout,
    isNewAddressBookLayoutFlag,
    isSignedIn,
    isSignedInWithData,
    loadingLazyComponent,
    loqateApiKey,
    items,
    postcodeOutOfAreaError,
    putDeliveryAddressApiCallActive,
    pathname,
    saveAddressError,
    selectedDeliveryAddress,
    servicesThatNeedRemoving,
    sessionId,
    shouldReFetchDeliveryOptions,
    showAddressBookModal,
    undeliverableItemsInBasket,
    userEmailAddress,
  ) => {
    const deliveryAddressFormValues = form?.[DELIVERY_ADDRESS_FORM_ID]?.values ?? {};
    const { countryCode } = deliveryAddressFormValues;

    const isCurrentlyDisabled =
      putDeliveryAddressApiCallActive ||
      deliveries?.some((delivery) => delivery?.getDeliveryMethodsApiCallActive ?? false) ||
      proceedingToPayment;

    const useThisAddressButtonSubmitting =
      !!createAddressAPICallActive || addressBookAPICallActive || putDeliveryAddressApiCallActive;

    const isNewAddressLayout = isNewAddressBookLayoutFlag && isSignedIn;

    const isLoadingLazyComponent = loadingLazyComponent === 'AddressBookModal';

    return {
      addressBook,
      addressSearchComplete,
      canUseSelectedDeliveryAddress,
      confirmedDeliveryAddress,
      countryCode,
      deliveries,
      deliveryContainsIncompatibleServices,
      deliveryContainsUndeliverableServices,
      incompleteDeliveryDetailsError,
      isChangingAddress,
      isCurrentlyDisabled,
      isExpressCheckout,
      isLoadingLazyComponent,
      isNewAddressLayout,
      isSignedIn,
      isSignedInWithData,
      items,
      loqateApiKey,
      pathname,
      postcodeOutOfAreaError,
      putDeliveryAddressError,
      saveAddressError,
      selectedDeliveryAddress,
      servicesThatNeedRemoving,
      sessionId,
      shouldReFetchDeliveryOptions,
      showAddressBookModal,
      undeliverableItemsInBasket,
      userEmailAddress,
      useThisAddressButtonSubmitting,
    };
  },
);

export default deliveryAddressState;
